import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Card, CardBody, CardFooter, Typography, Button, Alert, Spinner } from "@material-tailwind/react";
import api from "../utils/axiosConfig";
import AgentEvaluationForm, { AgentEvaluationFormData } from "../components/AgentEvaluationForm";
import PreMeetingLawyerEvaluationForm from "../components/PreMeetingLawyerEvaluationForm";

interface EvaluationData {
  token: string;
  hearing: {
    id: number;
    date: string;
    time: string;
    processNumber: string;
    company: {
      name: string;
    };
    hearingType?: {
      id: number;
      description: string;
    };
  };
  agent: {
    id: number;
    name: string;
  } | null;
  alreadySubmitted?: boolean; // Flag para indicar se avaliação já foi enviada
  overallRating?: number | null; // Para verificar se tem dados preenchidos
}

const AgentEvaluationPublic: React.FC = () => {
  const { token } = useParams<{ token: string }>();

  const [evaluationData, setEvaluationData] = useState<EvaluationData | null>(null);
  const [loading, setLoading] = useState(true);
  const [formState, setFormState] = useState<{ isValid: boolean; formattedData: AgentEvaluationFormData } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false); // Estado para controlar se já foi enviado

  useEffect(() => {
    const fetchEvaluationData = async () => {
      if (!token) return;
      
      try {
        setLoading(true);
        setError(null);
        const response = await api.get(`/agent-evaluations/token/${token}`);
        setEvaluationData(response.data);
        
        // Verificar se avaliação já foi submetida
        if (response.data?.alreadySubmitted || response.data?.overallRating !== null) {
          setAlreadySubmitted(true);
          setSuccess('Esta avaliação já foi enviada anteriormente. Não é possível enviar novamente.');
        }
      } catch (e: any) {
        console.error(e);
        setError(
          e?.response?.data?.message ||
          "Link de avaliação inválido ou expirado. Verifique o link e tente novamente."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchEvaluationData();
  }, [token]);

  const onSubmit = async () => {
    if (!token || !formState?.isValid) return;
    setSubmitting(true);
    setError(null);

    try {
      await api.post(`/agent-evaluations/token/${token}`, formState.formattedData);
      setSuccess("Avaliação enviada com sucesso. Obrigado pelo seu feedback!");
      setAlreadySubmitted(true);
    } catch (e: any) {
      console.error(e);
      setError(
        e?.response?.data?.message ||
          "Não foi possível enviar sua avaliação. Verifique sua conexão e tente novamente."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const isPreMeeting = evaluationData?.hearing?.hearingType?.id === 6;

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center w-full px-4 py-8 bg-gradient-to-b from-white to-preppoBlue-light/30">
        <img src="/logo-preppo.png" alt="logo preppo" className="h-16 md:h-20 w-auto mb-8" />
        <Spinner className="h-8 w-8 text-preppoBlue" />
        <Typography variant="small" className="mt-4 text-preppoBlue/80">
          Carregando informações da avaliação...
        </Typography>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center w-full px-4 py-8 bg-slate-50">
      <img src="/logo-preppo.png" alt="logo preppo" className="h-16 md:h-20 w-auto mb-6" />

      <Card className="w-full max-w-3xl rounded-2xl shadow-lg bg-white">
        <CardBody className="p-6 md:p-8">
          <Typography variant="h4" color="blue-gray" className="text-xl md:text-2xl mb-4 text-center">
            {isPreMeeting ? 'Avaliação do Preposto - Reunião Prévia' : 'Avaliação do Preposto'}
          </Typography>
          
          {evaluationData && (
            <div className="border-t border-gray-200 pt-4 mt-4">
              <Typography variant="h6" color="blue-gray" className="mb-3 font-medium">
                Informações da audiência, diligência ou reunião
              </Typography>
              <div className="space-y-2 text-sm text-gray-600">
                <div><strong>Empresa:</strong> {(evaluationData.hearing.company as any)?.tradeName || evaluationData.hearing.company?.name || 'Não informado'}</div>
                <div><strong>Processo:</strong> {evaluationData.hearing.processNumber || 'Não informado'}</div>
                <div><strong>Data:</strong> {(evaluationData.hearing as any).hearingDate ? new Date((evaluationData.hearing as any).hearingDate + 'T00:00:00').toLocaleDateString('pt-BR') : evaluationData.hearing.date ? new Date(evaluationData.hearing.date + 'T00:00:00').toLocaleDateString('pt-BR') : 'Não informado'}</div>
                <div><strong>Horário:</strong> {(evaluationData.hearing as any).hearingTime || evaluationData.hearing.time || 'Não informado'}</div>
                {evaluationData.agent && (
                  <div><strong>Preposto:</strong> {evaluationData.agent.name}</div>
                )}
                {(evaluationData.hearing as any).court && (
                  <div><strong>Vara:</strong> {(evaluationData.hearing as any).court}</div>
                )}
                {(evaluationData.hearing as any).district?.name && (
                  <div><strong>Cidade:</strong> {(evaluationData.hearing as any).district.name}</div>
                )}
                {(evaluationData.hearing as any).state?.name && (
                  <div><strong>Estado:</strong> {(evaluationData.hearing as any).state.name}</div>
                )}
              </div>
            </div>
          )}

          
          {success && (
            <Alert color="green" className="my-6">
              {success}
            </Alert>
          )}
          {error && (
            <Alert color="red" className="my-6">
              {error}
            </Alert>
          )}

          {!success && !alreadySubmitted && evaluationData && (
            <div className="border-t border-gray-200 pt-6 mt-6">
              <div className="[&_label]:mb-1 md:[&_label]:mb-1.5 [&_fieldset]:space-y-1 md:[&_fieldset]:space-y-2">
                {isPreMeeting ? (
                  <PreMeetingLawyerEvaluationForm onChange={setFormState as any} />
                ) : (
                  <AgentEvaluationForm onChange={setFormState as any} />
                )}
              </div>
            </div>
          )}
        </CardBody>
        {!success && !alreadySubmitted && evaluationData && (
          <CardFooter className="p-6 md:p-8 pt-0">
            <Button
              onClick={onSubmit}
              disabled={submitting || !formState?.isValid || alreadySubmitted}
              color="blue-gray"
              className="w-full"
              size="lg"
            >
              {submitting ? <Spinner className="h-4 w-4 mx-auto" /> : "Enviar Avaliação"}
            </Button>
          </CardFooter>
        )}
      </Card>

      <Typography variant="small" className="mt-4 text-preppoBlue/80">
        Em caso de dúvidas, entre em contato com o suporte Preppo.
      </Typography>
    </div>
  );
};

export default AgentEvaluationPublic;
